'use client'
import { useEffect, useRef, useState } from 'react'
import { useToastStore } from '@repo/store/useToastStore'
import { toast } from 'react-toastify'
import { useInitialDataFetchStore } from '@repo/store/useInitialDataFetchStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { confirm } from '@repo/ui/Confirm'

const OSPREY1_DEPLOYMENT_LOCALSTORAGE_KEY = 'osprey1Deployment'
const OSPREY1_CHECK_INTERVAL_MS = 1000 * 60 * 60

const clearAllSiteCookies = () => {
  const cookies = document.cookie ? document.cookie.split(';') : []
  for (const cookie of cookies) {
    const eqIndex = cookie.indexOf('=')
    const name = (eqIndex > -1 ? cookie.slice(0, eqIndex) : cookie).trim()
    if (!name) continue
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  }
}

const readCachedOsprey1Deployment = () => {
  try {
    const raw = localStorage.getItem(OSPREY1_DEPLOYMENT_LOCALSTORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const writeCachedOsprey1Deployment = ({ DeploymentId, DeploymentDate }) => {
  localStorage.setItem(OSPREY1_DEPLOYMENT_LOCALSTORAGE_KEY, JSON.stringify({ DeploymentId, DeploymentDate }))
}

export const CheckForLatestVersion = () => {
  const [buildWarningsShown, setBuildWarningsShown] = useState([])
  const { fetchInitialData } = useInitialDataFetchStore()
  const { showToast } = useToastStore()
  const osprey1PromptOpenRef = useRef(false)
  const buildNumberFromEnvVars = import.meta.env.VITE_SPA_OSPREY_BUILD_NUMBER || ''

  const env = import.meta.env.VITE_SPA_OSPREY_ENV || ''
  let urlModifier = ''

  if (env && env !== 'production') {
    urlModifier = `-${env}`
  }

  useEffect(() => {
    const checkReactVersionNumber = async () => {
      try {
        if (!buildNumberFromEnvVars) return

        // check for the deploymentInfo file of the react application
        const response = await fetch(
          `https://react${urlModifier}.ospreyapproach.com/deploymentInfo.json?v=${Date.now()}`,
          {
            headers: { accept: 'application/json' }
          }
        )

        const { Build } = await response.json()
        const reactBuildIsStale = Build !== buildNumberFromEnvVars

        console.log({ Build, buildNumberFromEnvVars, reactBuildIsStale })

        if (reactBuildIsStale) {
          showBuildWarning({ toastId: `new-app-version-${Build}` })
        }
      } catch (error) {
        console.error(error)
        showBuildWarning({ toastId: `new-app-version-react` })
      }
    }

    const showBuildWarning = ({ toastId }) => {
      console.log({ buildWarningsShown, toastId })
      if (buildWarningsShown.includes(toastId)) return

      toast.dismiss({ id: toastId })
      setBuildWarningsShown([...buildWarningsShown, toastId])

      console.log({ buildWarningsShown, toastId })

      showToast({
        id: toastId,
        type: 'info',
        text: 'A new version of this application is available. Please refresh your browser ASAP.',
        title: 'Update available',
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        actions: [
          {
            label: 'Refresh to apply update',
            onClick: async () => {
              toast.dismiss()
              await fetchInitialData()
              window.location.reload()
            }
          }
        ]
      })
    }

    const checkOsprey1VersionNumber = async () => {
      try {
        const { subdomain, isLocal } = useGlobalSettingsStore.getState()
        const osprey1Subdomain = isLocal ? 'dev---test' : subdomain
        if (!osprey1Subdomain) return

        const response = await fetch(
          `https://${osprey1Subdomain}.ospreyapproach.com/main/deploymentInfo.json?v=${Date.now()}`,
          {
            headers: { accept: 'application/json' }
          }
        )

        // Osprey1's deploymentInfo.json is written by PowerShell as UTF-16 LE, so decode explicitly
        const rawText = new TextDecoder('utf-16le').decode(await response.arrayBuffer()).replace(/^\uFEFF/, '')
        const { DeploymentDate, DeploymentId } = JSON.parse(rawText)

        console.log({ DeploymentDate, DeploymentId })
        const cached = readCachedOsprey1Deployment()

        if (!cached) {
          writeCachedOsprey1Deployment({ DeploymentId, DeploymentDate })
          return
        }

        const osprey1IsUnchanged = cached.DeploymentId === DeploymentId && cached.DeploymentDate === DeploymentDate
        if (osprey1IsUnchanged) return

        const { isLoggedIn } = useAuthenticationStore.getState()

        if (!isLoggedIn) {
          writeCachedOsprey1Deployment({ DeploymentId, DeploymentDate })
          clearAllSiteCookies()
          return
        }

        if (osprey1PromptOpenRef.current) return
        osprey1PromptOpenRef.current = true

        const proceed = await confirm(
          'An update to Osprey has been deployed. You need to log out so we can clear your cookies. Click Continue to log out now, or Cancel to keep working.'
        )

        osprey1PromptOpenRef.current = false

        if (!proceed) return

        writeCachedOsprey1Deployment({ DeploymentId, DeploymentDate })
        clearAllSiteCookies()
        await useAuthenticationStore.getState().logout()
        window.location.reload()
      } catch (error) {
        console.error(error)
      }
    }

    checkReactVersionNumber()
    checkOsprey1VersionNumber()

    const intervalDuration = 1000 * 60 * 20 // check every 20 mins
    const checkReactVersionInterval = setInterval(checkReactVersionNumber, intervalDuration)
    const checkOsprey1VersionInterval = setInterval(checkOsprey1VersionNumber, OSPREY1_CHECK_INTERVAL_MS)
    return () => {
      clearInterval(checkReactVersionInterval)
      clearInterval(checkOsprey1VersionInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildWarningsShown])
}
